import React from "react";
import { Box, Divider, IconButton, Skeleton, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AnnualPaymentCard from "../../../../components/AnnualPaymentCard/AnnualPaymentCard";
import ScratchPayPaymentCard from "../../../../components/ScratchPayPaymentCard/ScratchPayPaymentCard";
import { billCycle } from "../types";
import {
  ANNUAL_ENROLLMENT_TEXT,
  MESSAGES,
  getMonthlyScratchPayText,
  SECTION_TITLES,
} from "../constants";

interface IPaymentPlanSectionProps {
  annualPrice: number;
  monthlyPrice: number;
  billingCycle: billCycle;
  loading: boolean;
  eligibilityLoading: boolean;
  eligibilityError: any;
  eligible: boolean | undefined;
  isScratchPayDenied: boolean;
  prequalStatus?: "ELIGIBLE" | "INELIGIBLE";
  handleCardClick: (cycle: billCycle) => void;
  onInfoClick: () => void;
}

export const PaymentPlanSection = ({
  annualPrice,
  monthlyPrice,
  billingCycle,
  loading,
  eligibilityLoading,
  eligibilityError,
  eligible,
  isScratchPayDenied,
  prequalStatus,
  handleCardClick,
  onInfoClick,
}: IPaymentPlanSectionProps) => {
  const isPrequalIneligible = prequalStatus === "INELIGIBLE";

  const getScratchPayText = () => {
    if (isScratchPayDenied || isPrequalIneligible) return MESSAGES.SCRATCHPAY_DENIED;
    if (eligibilityLoading) return MESSAGES.ELIGIBILITY_CHECKING;
    if (eligibilityError) return MESSAGES.ELIGIBILITY_VERIFICATION_FAILED;
    return getMonthlyScratchPayText(`$${monthlyPrice.toFixed(0)}`);
  };

  const getScratchPayEligible = () => {
    if (eligibilityLoading || eligibilityError) return false;
    return (eligible ?? false) && !isScratchPayDenied && !isPrequalIneligible;
  };

  return (
    <section className="w-full px-4 flex flex-col gap-2">
      <Divider className="w-full" sx={{ borderColor: "taupe.dark", my: 1.5 }} />
      <Box className="flex items-center justify-between w-full">
        <Typography
          variant="body2"
          className="geograph-medium"
          sx={{
            color: "black.main",
            fontWeight: 600,
            lineHeight: "12px",
            fontSize: "12px",
          }}
        >
          {SECTION_TITLES.PAYMENT_PLAN}
        </Typography>
        <IconButton
          size="small"
          onClick={onInfoClick}
          className="!p-0"
          aria-label="ScratchPay info"
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "black.main" }} />
        </IconButton>
      </Box>
      {!loading ? (
        <Box className="flex flex-col gap-3 mt-2">
          <ScratchPayPaymentCard
            isSelected={billingCycle === "perMonth"}
            isEligible={getScratchPayEligible()}
            handleCardClick={() => handleCardClick("perMonth")}
            enrollmentText={getScratchPayText()}
            prequalifiedLabel={prequalStatus === "ELIGIBLE" ? "Already Pre-qualified!" : undefined}
          />
          <AnnualPaymentCard
            price={annualPrice}
            isSelected={billingCycle === "perYear"}
            handleCardClick={() => handleCardClick("perYear")}
            enrollmentText={ANNUAL_ENROLLMENT_TEXT}
          />
        </Box>
      ) : (
        <Skeleton animation="wave" height={100} />
      )}
    </section>
  );
};
